import React, { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";

// Suppress global Mermaid parse error overlay
if (!(window as any).mermaidErrorPatched) {
  (window as any).mermaidErrorPatched = true;
  (mermaid as any).parseError = () => {
    /* swallow to prevent default overlay */
  };
}

interface MermaidBlock {
  title: string;
  code: string;
}

const extractMermaidBlocks = (content: string): MermaidBlock[] => {
  const blocks: MermaidBlock[] = [];
  const lines = content.split("\n");
  let inBlock = false;
  let currentBlock = "";
  let lastHeader = "";
  let currentTitle = "";

  // Check if this is raw Mermaid syntax (starts with graph, flowchart, etc.)
  const trimmedContent = content.trim();
  const mermaidKeywords = [
    "graph",
    "flowchart",
    "sequenceDiagram",
    "classDiagram",
    "stateDiagram",
    "erDiagram",
    "gantt",
    "pie",
    "gitgraph",
  ];
  const isRawMermaid = mermaidKeywords.some((keyword) =>
    trimmedContent.startsWith(keyword)
  );

  if (isRawMermaid) {
    // Handle raw Mermaid syntax - treat entire content as one block
    blocks.push({ title: "Infrastructure Diagram", code: trimmedContent });
    return blocks;
  }

  // Handle markdown with ```mermaid blocks
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("#")) {
      lastHeader = trimmed.replace(/^#+\s*/, "").trim();
    } else if (trimmed === "```mermaid") {
      inBlock = true;
      currentBlock = "";
      currentTitle = lastHeader || "Diagram";
    } else if (inBlock && trimmed === "```") {
      inBlock = false;
      blocks.push({ title: currentTitle, code: currentBlock.trim() });
      currentTitle = "";
    } else if (inBlock) {
      currentBlock += line + "\n";
    }
  }
  return blocks;
};

const SingleMermaid: React.FC<{ title: string; code: string }> = ({
  title,
  code,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !code) return;
    containerRef.current.innerHTML = "";
    try {
      // First parse with suppressErrors
      // mermaid.parse throws on syntax error; no need to inspect return value
      mermaid.parse(code, { suppressErrors: true });

      // Then render
      const renderRes = mermaid.render(
        `mermaid-${Math.random().toString(36).substring(7)}`,
        code
      ) as any;
      if (renderRes.then) {
        renderRes
          .then((res: any) => {
            containerRef.current!.innerHTML = res.svg || res;
          })
          .catch(() => setError(true));
      } else {
        containerRef.current!.innerHTML = renderRes.svg || renderRes;
      }
    } catch (err) {
      console.error("Mermaid render error", err);
      setError(true);
    }
  }, [code]);

  if (error) {
    return (
      <div className="flex items-center justify-center w-full h-48 text-gray-500 border border-red-200 p-4 rounded">
        Failed to render diagram: {title || "Untitled"}
      </div>
    );
  }

  return (
    <div className="my-4">
      {title && <h3 className="text-lg font-semibold mb-2">{title}</h3>}
      <div ref={containerRef} style={{ width: "100%" }} />
    </div>
  );
};

interface TerraformMermaidProps {
  definition: string; // raw markdown content with mermaid blocks
}

mermaid.initialize({ startOnLoad: false, suppressErrorRendering: true });

const TerraformMermaid: React.FC<TerraformMermaidProps> = ({ definition }) => {
  const blocks = React.useMemo(
    () => extractMermaidBlocks(definition),
    [definition]
  );

  if (blocks.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-full text-gray-500">
        No infrastructure diagrams found.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {blocks.map((block, index) => (
        <SingleMermaid key={index} title={block.title} code={block.code} />
      ))}
    </div>
  );
};

export default TerraformMermaid;
